/**
 * İÇE AKTAR — dosyadan hesap ve hareket (09.10.2026).
 * Biçim: `claude/notlar/2026-10-09-ice-aktarma-onerisi.md`; okuma `lib/iceAktarma.ts`.
 *
 * Sıra: dosya → sayı biçimi → hesaplar (yeni mi, var olan mı) → varlıklar
 * (katalogda eşleştirme; tahmin yok, belirsizde kullanıcı seçer) → önizleme
 * (hiçbir şey yazılmadan: yeni / güncellenecek / aynı / silinmiş) → TEK
 * işlemde yazım → kullanılan araçlar fiyat izlemesine alınır.
 *
 * Fiyatı boş TEFAS satırı (yalnızca bunu bildiren kaynak biçimlerinde): fon
 * eşleşince merkezde izlemeye alınır ve o günün fiyatı `/prices`tan okunur.
 * Yalnızca O GÜNÜN fiyatı; yoksa satır sebebiyle dışarıda kalır.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { AssetBinding, Coverage, DailyBar, Decimal, IsoDate } from "@parafon/contracts";
import type { ResolveCandidateDto } from "@parafon/contracts/central-api";
import { US_EXCHANGES } from "@parafon/contracts/central-api";
import type { AccountKind, IceAktarmaDurumu, IceAktarmaSonucu, NewAccount, NewTransaction } from "@parafon/contracts/local-api";
import { useCentral } from "../central/CentralContext";
import { M } from "../dil";
import { CurrencySelect } from "../ui/CurrencySelect";
import { EkstreMaskeKarti } from "../components/EkstreMaskeKarti";
import { SatirGeriBildirimi } from "../components/SatirGeriBildirimi";
import type { GeriBildirimSatiri } from "../lib/geriBildirim";
import { downloadBlob, readFileAsBytes } from "../lib/files";
import { errorText } from "../lib/rpcError";
import { cashLabel } from "../lib/txShape";
import {
  GERI_YATIRIM,
  csvYaz,
  hareketKimlikleri,
  hesapAnahtari,
  hesapKimligiTuret,
  metniCoz,
  sablonCsv,
  varlikAnahtari,
  type OkunanSatir,
  type SayiBicimi,
  type VarlikAdi,
} from "../lib/iceAktarma";
import { arsivFiyatiBul, arsivNotu, dosyaOku } from "../lib/parafonOncesi";
import { MIDAS_AD, midasOku, ozetKarsilastir, type MidasBelgesi, type MidasOkuma, type OzetKarsilastirma } from "../lib/midasEkstre";
import { pdfOkuyucu } from "../lib/pdfMetin";
import { parafonOncesiPdf } from "../lib/parafonOncesiPdf";
import { assetKey } from "../lib/format";
import { UyariIkonu } from "../ui/UyariIkonu";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useMutation } from "../repo/useAsync";
import { useSinirlar } from "../kabiliyet/KabiliyetSaglayici";
import { KilitliKabiliyet } from "../kabiliyet/KilitliKabiliyet";
import { Button, Field, InlineError, Notice, Screen, Select, Spinner } from "../ui/primitives";


const DURUM_ADI: Readonly<Record<IceAktarmaDurumu, string>> = M.iceAktar.ekran.durum;

/**
 * Hesap eşleme. Yeni hesabın adı kullanıcının (varsayılanı dosyadaki ad);
 * dosyadaki ad hesabın aracı kurum alanına yazılır (kullanıcı 25.10.2026).
 * Aynı kurumdan birden çok hesap varsa seçim kullanıcıya bırakılır.
 */
type HesapSecimi =
  | { readonly tur: "yeni"; readonly kind: AccountKind; readonly currency: string; readonly ad: string }
  | { readonly tur: "var"; readonly id: string }
  | { readonly tur: "secilmedi" };

interface MevcutHesap {
  readonly id: string;
  readonly name: string;
  readonly institution: string | null;
  /** Devre dışı hesaba yükleme serbest, ama uyarılır (kullanıcı kararı 25.10.2026). */
  readonly devreDisi: boolean;
}

/** Dosyadaki hesap adı, var olan hesabın adı ya da kurum adıyla eşleşiyor mu. */
function ayniKurum(a: MevcutHesap, ad: string): boolean {
  const k = ad.toLocaleLowerCase("tr");
  return a.name.toLocaleLowerCase("tr") === k || (a.institution ?? "").toLocaleLowerCase("tr") === k;
}

type ArsivDurumu =
  | { readonly tur: "yukleniyor" }
  | { readonly tur: "hazir"; readonly bars: readonly DailyBar[]; readonly kapsam: Coverage | null }
  | { readonly tur: "hata"; readonly sebep: string };

type VarlikDurumu =
  | { readonly tur: "araniyor" }
  | { readonly tur: "aday"; readonly adaylar: readonly ResolveCandidateDto[]; readonly secili: string | null }
  | { readonly tur: "yok"; readonly sebep: string }
  | { readonly tur: "elle" };

/** Piyasa adından katalog süzgeci: kullanıcının yazdığı piyasa, adayın sınıfı ya da borsası. */
function piyasayaUyar(piyasa: string | null, c: ResolveCandidateDto): boolean {
  if (piyasa === null) return true;
  const ex = c.exchange.toUpperCase();
  switch (piyasa) {
    case "TEFAS":
      return c.assetClass === "fund_tr" || ex === "TEFAS";
    case "BIST":
      return c.assetClass === "equity_tr" || ex === "BIST";
    case "ETF":
      return c.assetClass === "etf";
    // Kurum ekstresindeki TL menkulü (Midas, 25.10.2026): ekstre hisse ile fonu
    // ayırmaz — BIST hissesi ya da TEFAS fonu; ikisi de varsa kullanıcı seçer.
    case "TR":
      return c.assetClass === "equity_tr" || c.assetClass === "fund_tr" || ex === "BIST" || ex === "TEFAS";
    // Kurum ekstresindeki ABD menkulü (Midas, 22.10.2026): dolar, ETF ya da yabancı hisse.
    // Hiç izlenmemiş aracın para birimi katalogda bilinmez ("?", 25.10.2026 VIXY):
    // o zaman ABD borsasında olması yeter; opsiyon (OPR) ve yabancı kopya elenir.
    case "ABD":
      return (
        (c.assetClass === "etf" || c.assetClass === "equity_foreign") &&
        (c.currency === "USD" || ((c.currency === "?" || c.currency === "") && US_EXCHANGES.includes(ex)))
      );
    default:
      return ex === piyasa || ex.includes(piyasa);
  }
}

function enSik(degerler: readonly string[], varsayilan: string): string {
  const say = new Map<string, number>();
  for (const d of degerler) say.set(d, (say.get(d) ?? 0) + 1);
  let en = varsayilan;
  let n = 0;
  for (const [d, k] of say) if (k > n) [en, n] = [d, k];
  return en;
}

interface Paket {
  readonly accounts: NewAccount[];
  readonly transactions: NewTransaction[];
  /** Eşleşmeyen varlık ya da bulunamayan arşiv fiyatı yüzünden dışarıda kalan satırlar. */
  readonly disarida: readonly { readonly s: OkunanSatir; readonly sebep: string; readonly bekleniyor: boolean }[];
  /** Fiyatı TEFAS arşivinden alınan satır sayısı. */
  readonly arsivden: number;
  readonly izlenecek: readonly ResolveCandidateDto[];
}

export function IceAktarScreen() {
  const { repo, invalidate, revision } = useRepositoryContext();
  // Gelir modeli: CSV ve "Parafon öncesi" dosyalar `iceAktarma`, kurum
  // ekstresi `kurumEkstresi`; yeni hesaplar hesap sınırına sayılır. Dosya
  // okunur ve önizlenir; yalnızca yazmak kilitli.
  // Etkin hesap sayısı ekranın zaten okuduğu listeden (`mevcutHesaplar`):
  // ayrı bir eşzamansız okuma yazım sırasında araya çizim sokup hesap adı
  // alanındaki tuşları yutuyordu (yük altında yeniden üretildi, 26.10.2026).
  const sinir = useSinirlar();
  const { central, prices } = useCentral();
  const [dosya, setDosya] = useState<{ ad: string; metin: string; kodlama: string } | null>(null);
  // Kurum ekstresi (PDF, 22.10.2026): okunmuş hâli; parola bekleyen dosyalar; özetle karşılaştırma.
  const [midas, setMidas] = useState<MidasOkuma | null>(null);
  const [pdfBekleyen, setPdfBekleyen] = useState<{ dosyalar: readonly { ad: string; bayt: Uint8Array }[]; yanlis: boolean } | null>(null);
  const [pdfParola, setPdfParola] = useState("");
  const [pdfHata, setPdfHata] = useState<string | null>(null);
  const [ozetSonucu, setOzetSonucu] = useState<readonly OzetKarsilastirma[] | null>(null);
  const [bicim, setBicim] = useState<SayiBicimi | null>(null);
  const [mevcutHesaplar, setMevcutHesaplar] = useState<readonly MevcutHesap[]>([]);
  const [hesapSecimi, setHesapSecimi] = useState<Record<string, HesapSecimi>>({});
  // Kullanıcının elle değiştirdiği hesap seçimleri: varsayılan yeniden hesaplanırken korunur.
  const elleSecilen = useRef(new Set<string>());
  const hesapSec = (anahtar: string, sec: HesapSecimi): void => {
    elleSecilen.current.add(anahtar);
    setHesapSecimi((d) => ({ ...d, [anahtar]: sec }));
  };
  const [varlikDurumu, setVarlikDurumu] = useState<Record<string, VarlikDurumu>>({});
  const [arsiv, setArsiv] = useState<Record<string, ArsivDurumu>>({});
  const [paket, setPaket] = useState<Paket | null>(null);
  const [onizleme, setOnizleme] = useState<IceAktarmaSonucu | null>(null);
  const [onizlemeHatasi, setOnizlemeHatasi] = useState<unknown>(undefined);
  const [sonuc, setSonuc] = useState<{ s: IceAktarmaSonucu; izlenemeyen: string[] } | null>(null);
  const okuRun = useMutation();
  const yazRun = useMutation();

  // Var olan hesaplar: kasa değişince ve her yeni dosyada yeniden okunur
  // (başka bir sekmede eklenen hesap eşleştirmede görünsün).
  const hesaplariYukle = (): Promise<void> =>
    repo.accounts
      .list(false, true)
      .then(
        (l) => setMevcutHesaplar(l.map((a) => ({ id: a.id, name: a.name, institution: a.institution ?? null, devreDisi: a.devreDisi === true }))),
        () => undefined,
      );
  useEffect(() => {
    void hesaplariYukle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo, revision]);

  const okuma = useMemo(
    (): ReturnType<typeof dosyaOku> | null =>
      midas ?? (dosya === null || bicim === null ? null : dosyaOku(dosya.metin, bicim)),
    [midas, dosya, bicim],
  );

  const hesaplar = useMemo(() => {
    const m = new Map<string, { anahtar: string; ad: string; kimlik: string | null; paralar: string[] }>();
    for (const s of okuma?.satirlar ?? []) {
      const a = hesapAnahtari(s);
      const v = m.get(a) ?? { anahtar: a, ad: s.hesapAdi, kimlik: s.hesapKimligi, paralar: [] };
      v.paralar.push(s.paraBirimi);
      m.set(a, v);
    }
    return [...m.values()];
  }, [okuma]);

  const varliklar = useMemo(() => {
    const m = new Map<string, { anahtar: string; v: VarlikAdi; paralar: string[] }>();
    for (const s of okuma?.satirlar ?? []) {
      if (s.varlik === null) continue;
      const a = varlikAnahtari(s.varlik);
      const v = m.get(a) ?? { anahtar: a, v: s.varlik, paralar: [] };
      v.paralar.push(s.paraBirimi);
      m.set(a, v);
    }
    // Ekstrenin portföy özetindeki varlıklar da eşleşir: karşılaştırma kasadaki
    // varlıkla yapılır (hareketi bu ekstrede olmasa bile).
    for (const o of midas?.ozet?.satirlar ?? []) {
      const v: VarlikAdi = { piyasa: o.piyasa, kod: o.sembol };
      const a = varlikAnahtari(v);
      if (!m.has(a)) m.set(a, { anahtar: a, v, paralar: [o.paraBirimi] });
    }
    return [...m.values()];
  }, [okuma, midas]);

  // Hesap varsayılanları: adı ya da kurumu aynı tek hesap varsa o; birden çoksa
  // kullanıcı seçer; yoksa yeni (dosyadaki adla, en sık para birimiyle).
  useEffect(() => {
    setHesapSecimi((onceki) => {
      const out: Record<string, HesapSecimi> = {};
      for (const h of hesaplar) {
        const eski = onceki[h.anahtar];
        if (eski !== undefined && elleSecilen.current.has(h.anahtar)) out[h.anahtar] = eski;
        else {
          const ayni = mevcutHesaplar.filter((a) => ayniKurum(a, h.ad));
          out[h.anahtar] =
            ayni.length === 1
              ? { tur: "var", id: (ayni[0] as MevcutHesap).id }
              : ayni.length > 1
                ? { tur: "secilmedi" }
                : { tur: "yeni", kind: "araci_kurum", currency: enSik(h.paralar, "TRY"), ad: h.ad };
        }
      }
      return out;
    });
  }, [hesaplar, mevcutHesaplar]);

  // Varlık eşleştirme: anahtar başına bir kez arama.
  useEffect(() => {
    let iptal = false;
    for (const v of varliklar) {
      if (varlikDurumu[v.anahtar] !== undefined) continue;
      if (central === null) {
        setVarlikDurumu((d) => ({ ...d, [v.anahtar]: { tur: "yok", sebep: M.iceAktar.ekran.merkezYokKatalog } }));
        continue;
      }
      setVarlikDurumu((d) => ({ ...d, [v.anahtar]: { tur: "araniyor" } }));
      void central.resolve(v.v.kod).then(
        (r) => {
          if (iptal) return;
          const uyan = r.candidates.filter((c) => c.symbol.toUpperCase() === v.v.kod.toUpperCase() && piyasayaUyar(v.v.piyasa, c));
          const sebep =
            r.remoteLookup === "butce-yok" || r.remoteLookup === "hata"
              ? M.iceAktar.ekran.katalogdaYokDisArama
              : M.iceAktar.ekran.katalogdaBulunamadi;
          setVarlikDurumu((d) => ({
            ...d,
            [v.anahtar]: uyan.length === 0 ? { tur: "yok", sebep } : { tur: "aday", adaylar: uyan, secili: uyan.length === 1 ? (uyan[0]?.instrumentId ?? null) : null },
          }));
        },
        (e: unknown) => {
          if (!iptal) setVarlikDurumu((d) => ({ ...d, [v.anahtar]: { tur: "yok", sebep: M.iceAktar.ekran.aramaYapilamadi(errorText(e)) } }));
        },
      );
    }
    return () => {
      iptal = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [varliklar, central]);

  const varlikBaglami = (v: VarlikAdi, para: string): AssetBinding | null => {
    const d = varlikDurumu[varlikAnahtari(v)];
    if (d?.tur === "elle") return { kind: "manual", label: v.piyasa === null ? v.kod : `${v.piyasa}:${v.kod}`, currency: para };
    if (d?.tur === "aday" && d.secili !== null) return { kind: "tracked", instrumentId: d.secili };
    return null;
  };

  // Arşiv fiyatı gereken araçlar ve tarih aralıkları (eşleşmiş, izlenen araçlar).
  const arsivIhtiyaci = useMemo(() => {
    const m = new Map<string, { from: IsoDate; to: IsoDate }>();
    for (const s of okuma?.satirlar ?? []) {
      if (!s.fiyatArsivden || s.varlik === null) continue;
      const d = varlikDurumu[varlikAnahtari(s.varlik)];
      if (d?.tur !== "aday" || d.secili === null) continue;
      const a = m.get(d.secili);
      m.set(d.secili, a === undefined ? { from: s.tarih, to: s.tarih } : { from: s.tarih < a.from ? s.tarih : a.from, to: s.tarih > a.to ? s.tarih : a.to });
    }
    return m;
  }, [okuma, varlikDurumu]);

  useEffect(() => {
    for (const [id, aralik] of arsivIhtiyaci) {
      if (arsiv[id] !== undefined) continue;
      if (central === null) {
        setArsiv((a) => ({ ...a, [id]: { tur: "hata", sebep: M.iceAktar.ekran.merkezYokTefas } }));
        continue;
      }
      setArsiv((a) => ({ ...a, [id]: { tur: "yukleniyor" } }));
      void (async () => {
        try {
          // Geçmiş ancak talep edilen araç için tutulur: önce izlemeye al.
          await central.track(id);
          const r = await central.prices([id], aralik.from, aralik.to);
          const seri = r.series.find((x) => x.instrumentId === id);
          setArsiv((a) => ({ ...a, [id]: { tur: "hazir", bars: seri?.bars ?? [], kapsam: seri?.coverage ?? null } }));
        } catch (e) {
          setArsiv((a) => ({ ...a, [id]: { tur: "hata", sebep: M.iceAktar.ekran.tefasSorulamadiHata(errorText(e)) } }));
        }
      })();
    }
    // `arsiv` bağımlılıkta: "yeniden sor" durumu boşaltınca sorular yeniden gider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arsivIhtiyaci, central, arsiv]);

  const hazir =
    okuma !== null &&
    [...arsivIhtiyaci.keys()].every((id) => arsiv[id] !== undefined && arsiv[id]?.tur !== "yukleniyor") &&
    hesaplar.every((h) => {
      const sec = hesapSecimi[h.anahtar];
      return sec !== undefined && sec.tur !== "secilmedi" && !(sec.tur === "yeni" && sec.ad.trim() === "");
    }) &&
    varliklar.every((v) => {
      const d = varlikDurumu[v.anahtar];
      return d !== undefined && d.tur !== "araniyor" && !(d.tur === "aday" && d.secili === null);
    });

  // Paketi kur ve önizle (hiçbir şey yazılmaz).
  useEffect(() => {
    if (!hazir || okuma === null) {
      setPaket(null);
      setOnizleme(null);
      return;
    }
    let iptal = false;
    void (async () => {
      const hesapId = new Map<string, string>();
      const accounts: NewAccount[] = [];
      for (const h of hesaplar) {
        const sec = hesapSecimi[h.anahtar] as HesapSecimi;
        if (sec.tur === "secilmedi") continue;
        if (sec.tur === "var") hesapId.set(h.anahtar, sec.id);
        else {
          const id = await hesapKimligiTuret(h.anahtar);
          hesapId.set(h.anahtar, id);
          // Dosyadaki ad aracı kurum adı olarak kalır, kullanıcı hesabı başka adlandırsa da.
          accounts.push({ id, name: sec.ad.trim(), institution: h.ad, kind: sec.kind, currency: sec.currency });
        }
      }
      const kimlikler = await hareketKimlikleri(okuma.satirlar);
      const transactions: NewTransaction[] = [];
      const disarida: { s: OkunanSatir; sebep: string; bekleniyor: boolean }[] = [];
      let arsivden = 0;
      const izlenecek = new Map<string, ResolveCandidateDto>();
      for (const s of okuma.satirlar) {
        const accountId = hesapId.get(hesapAnahtari(s)) as string;
        const [k1, k2] = kimlikler.get(s.satirNo) ?? [];
        const ortak = {
          accountId,
          currency: s.paraBirimi,
          tradeDate: s.tarih,
          ...(s.not === null ? {} : { note: s.not }),
        };
        let asset: AssetBinding;
        if (s.varlik === null) asset = { kind: "manual", label: cashLabel(s.paraBirimi), currency: s.paraBirimi };
        else {
          const b = varlikBaglami(s.varlik, s.paraBirimi);
          if (b === null) {
            disarida.push({ s, sebep: M.iceAktar.ekran.varlikEslestirilmedi, bekleniyor: false });
            continue;
          }
          asset = b;
          if (b.kind === "tracked") {
            const d = varlikDurumu[varlikAnahtari(s.varlik)];
            const c = d?.tur === "aday" ? d.adaylar.find((x) => x.instrumentId === b.instrumentId) : undefined;
            if (c !== undefined) izlenecek.set(c.instrumentId, c);
          }
        }
        let fiyat = s.fiyat;
        let not = ortak.note;
        if (s.fiyatArsivden) {
          const a = asset.kind === "tracked" ? arsiv[asset.instrumentId] : undefined;
          const f =
            asset.kind !== "tracked"
              ? ({ tur: "yok", sebep: M.iceAktar.ekran.elleTefasYok } as const)
              : a?.tur === "hazir"
                ? arsivFiyatiBul(s.tarih, a.bars, a.kapsam)
                : ({ tur: "bekleniyor", sebep: a?.tur === "hata" ? a.sebep : M.iceAktar.ekran.tefasSorulamadi } as const);
          if (f.tur !== "var") {
            disarida.push({ s, sebep: f.sebep, bekleniyor: f.tur === "bekleniyor" });
            continue;
          }
          fiyat = f.fiyat;
          not = [ortak.note, arsivNotu(s.tarih)].filter((x) => x !== undefined).join(" · ");
          arsivden += 1;
        }
        const valor = s.valor === null ? {} : { settleDate: s.valor };
        const vergi = s.vergi === null ? {} : { tax: s.vergi };
        const masraf = s.masraf === null ? {} : { fee: s.masraf };
        if (s.tur === GERI_YATIRIM) {
          const tutar = s.temettuTutari as Decimal;
          transactions.push({ id: k1 as string, ...ortak, asset, type: "DIVIDEND", quantity: "1", price: tutar, ...vergi, ...valor });
          // Alışın tamamı temettüyle: "geçmiş dahil getiri %"nin paydasına girmez (19.10.2026).
          transactions.push({ id: k2 as string, ...ortak, asset, type: "BUY", quantity: s.miktar as Decimal, price: s.fiyat as Decimal, temettuden: true });
        } else if (s.tur === "FX_CONVERSION" && s.tutar !== null && s.alinanTutar !== null && s.alinanParaBirimi !== null) {
          transactions.push({
            id: k1 as string,
            ...ortak,
            asset,
            type: "FX_CONVERSION",
            quantity: "1",
            price: s.tutar,
            alinanTutar: s.alinanTutar,
            alinanParaBirimi: s.alinanParaBirimi,
            ...masraf,
          });
        } else if (s.tur === "TAX_PAYMENT" && s.tutar !== null) {
          // Dosyadan gelen vergi ödemesi hesaptaki nakitten ve ödendiği yıla (25.10.2026).
          transactions.push({
            id: k1 as string,
            ...ortak,
            asset,
            type: "TAX_PAYMENT",
            quantity: "1",
            price: s.tutar,
            vergiYili: Number(s.tarih.slice(0, 4)),
            vergiKaynagi: "nakit",
          });
        } else if (s.tutar !== null && ["DIVIDEND", "INTEREST", "DEPOSIT", "WITHDRAWAL", "FEE"].includes(s.tur)) {
          transactions.push({ id: k1 as string, ...ortak, asset, type: s.tur, quantity: "1", price: s.tutar, ...vergi, ...valor });
        } else {
          transactions.push({
            id: k1 as string,
            ...ortak,
            ...(not === undefined ? {} : { note: not }),
            asset,
            type: s.tur,
            quantity: s.miktar as Decimal,
            price: fiyat ?? "0",
            ...masraf,
            ...vergi,
            ...valor,
          });
        }
      }
      // Hiç hareketi pakete girmeyen yeni hesap eklenmez (tüm satırları dışarıda kaldıysa).
      const kullanilan = new Set(transactions.map((t) => t.accountId));
      const p: Paket = {
        accounts: accounts.filter((a) => kullanilan.has(a.id)),
        transactions,
        disarida,
        arsivden,
        izlenecek: [...izlenecek.values()],
      };
      if (iptal) return;
      setPaket(p);
      try {
        const o = await repo.iceAktar({ accounts: p.accounts, transactions }, { deneme: true });
        if (!iptal) {
          setOnizleme(o);
          setOnizlemeHatasi(undefined);
        }
      } catch (e) {
        if (!iptal) {
          setOnizleme(null);
          setOnizlemeHatasi(e);
        }
      }
      // EKSTRENİN PORTFÖY ÖZETİ ile kasa (bu hesap, özet gününe kadar; içe
      // aktarılacaklar dahil). Tutmayan varlık uyarıyla gösterilir.
      const ozet = midas?.ozet ?? null;
      if (ozet === null) {
        if (!iptal) setOzetSonucu(null);
        return;
      }
      const hesapIdleri = [...new Set(transactions.map((t) => t.accountId))];
      const yeniIdler = new Set(transactions.map((t) => t.id));
      const mevcut =
        hesapIdleri.length === 0
          ? []
          : (await repo.transactions.query({ accountIds: hesapIdleri, to: ozet.tarih })).rows.filter((t) => !yeniIdler.has(t.id));
      const hareketler = [...mevcut, ...transactions].map((t) => ({
        type: t.type,
        tradeDate: t.tradeDate,
        quantity: t.quantity,
        varlik: assetKey(t.asset),
      }));
      const sonuc = ozetKarsilastir(ozet, hareketler, (o) => {
        const b = varlikBaglami({ piyasa: o.piyasa, kod: o.sembol }, o.paraBirimi);
        return b === null ? null : assetKey(b);
      });
      if (!iptal) setOzetSonucu(sonuc);
    })();
    return () => {
      iptal = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hazir, okuma, hesapSecimi, varlikDurumu, arsiv, repo, revision]);

  const sayim = (r: Readonly<Record<string, IceAktarmaDurumu>> | undefined): Record<IceAktarmaDurumu, number> => {
    const n = { yeni: 0, guncellenecek: 0, ayni: 0, silinmis: 0 };
    for (const d of Object.values(r ?? {})) n[d] += 1;
    return n;
  };
  const tSay = sayim(onizleme?.transactions);
  const hSay = sayim(onizleme?.accounts);
  const yazilacak = tSay.yeni + tSay.guncellenecek + hSay.yeni + hSay.guncellenecek;
  const iceAktarmaKilidi =
    okuma === null
      ? null
      : midas !== null
        ? sinir.kurumEkstresi
          ? null
          : M.iceAktar.ekran.kilitKurumEkstresi
        : sinir.iceAktarma
          ? null
          : M.iceAktar.ekran.kilitDosya;
  const etkinHesapSayisi = mevcutHesaplar.filter((a) => !a.devreDisi).length;
  const hesapKilidi = sinir.hesap !== null && etkinHesapSayisi + hSay.yeni > sinir.hesap;
  const yazmaKilitli = iceAktarmaKilidi !== null || hesapKilidi;

  const sifirla = (): void => {
    elleSecilen.current.clear();
    void hesaplariYukle();
    setSonuc(null);
    setVarlikDurumu({});
    setArsiv({});
    setHesapSecimi({});
    setOzetSonucu(null);
    setPdfHata(null);
  };

  /**
   * Kurum ekstreleri (PDF): cihazda okunur, hiçbir yere gönderilmez. Parolalı
   * dosya varsa parola sorulur (yalnızca açmak için, saklanmaz); aynı parola
   * hepsine denenir.
   */
  const pdfleriOku = async (dosyalar: readonly { ad: string; bayt: Uint8Array }[], parola?: string): Promise<void> => {
    sifirla();
    setMidas(null);
    setDosya(null);
    const belgeler: MidasBelgesi[] = [];
    for (const d of dosyalar) {
      const r = await pdfOkuyucu.oku(d.bayt, parola);
      if (r.tur === "parola") {
        setPdfBekleyen({ dosyalar, yanlis: r.yanlis });
        return;
      }
      if (r.tur === "metin-yok") {
        setPdfBekleyen(null);
        setPdfHata(M.iceAktar.ekran.pdfMetinYok(d.ad));
        return;
      }
      if (r.tur === "okunamadi") {
        setPdfBekleyen(null);
        setPdfHata(`${d.ad}: ${r.sebep}`);
        return;
      }
      belgeler.push({ ad: d.ad, sayfalar: r.sayfalar });
    }
    setPdfBekleyen(null);
    setPdfParola("");
    const m = midasOku(belgeler);
    if (m.tur !== "tamam") {
      // Parafon öncesi kayıt dökümünün PDF'i (geri bildirim, 26.10.2026): aynı
      // tablo CSV'ye çevrilir, CSV dosyası gibi okunur.
      const oncesi = parafonOncesiPdf(belgeler);
      if (oncesi !== null) {
        setBicim("tr");
        setDosya({ ad: dosyalar.map((d) => d.ad).join(", "), metin: oncesi.metin, kodlama: "pdf" });
        return;
      }
      setPdfHata(M.iceAktar.ekran.ekstreTaninmadi(m.sebep));
      return;
    }
    setBicim("tr");
    setMidas(m.okuma);
  };

  const onDosyalar = (fs: readonly File[]): void => {
    void okuRun.run(async () => {
      const pdfMi = (f: File) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
      if (fs.length > 1 && !fs.every(pdfMi)) {
        sifirla();
        setPdfHata(M.iceAktar.ekran.karisikDosya);
        return;
      }
      const ilk = fs[0];
      if (ilk === undefined) return;
      if (pdfMi(ilk)) {
        const dosyalar = await Promise.all(fs.map(async (f) => ({ ad: f.name, bayt: await readFileAsBytes(f) })));
        await pdfleriOku(dosyalar);
        return;
      }
      const { metin, kodlama } = metniCoz(await readFileAsBytes(ilk));
      sifirla();
      setMidas(null);
      setPdfBekleyen(null);
      setDosya({ ad: ilk.name, metin, kodlama });
      // Öneri varsa o seçili gelir; kullanıcı değiştirebilir. Yoksa Türkçe.
      setBicim(dosyaOku(metin, "tr").bicimOnerisi ?? "tr");
    });
  };

  const onYaz = (): void => {
    if (paket === null || yazmaKilitli) return;
    void yazRun.run(async () => {
      const s = await repo.iceAktar({ accounts: paket.accounts, transactions: paket.transactions });
      const izlenemeyen: string[] = [];
      if (central !== null) {
        for (const c of paket.izlenecek) {
          try {
            const st = await central.track(c.instrumentId);
            await prices?.putInstrument({
              instrumentId: c.instrumentId,
              symbol: c.symbol,
              exchange: c.exchange,
              assetClass: c.assetClass,
              currency: c.currency,
              name: c.name,
              centralState: st.state,
              backfilledTo: st.backfilledTo,
              horizonFrom: st.horizonFrom,
              pendingJobs: st.pendingJobs,
              etaSeconds: st.etaSeconds,
              ...(st.lastError === undefined ? {} : { lastError: st.lastError }),
            });
          } catch {
            izlenemeyen.push(c.symbol);
          }
        }
      }
      setSonuc({ s, izlenemeyen });
      invalidate();
    });
  };

  // Geliştirmeye gönderilecek satırlar (25.10.2026): okunamayanlar ve dışarıda kalanlar.
  const geriBildirimSatirlari = useMemo<readonly GeriBildirimSatiri[]>(
    () =>
      okuma === null
        ? []
        : [
            ...okuma.sorunlar.map((s) => ({ kaynak: s.kaynak, baslik: okuma.baslik, ham: s.ham, sebep: s.mesajlar.join(" · ") })),
            ...(paket?.disarida ?? []).map((d) => ({ kaynak: undefined, baslik: okuma.baslik, ham: d.s.ham, sebep: d.sebep })),
          ],
    [okuma, paket],
  );

  const sorunluIndir = (): void => {
    if (okuma === null) return;
    const satirlar = [
      [...okuma.baslik, M.iceAktar.ekran.sorunSutunu],
      ...okuma.sorunlar.map((s) => [...s.ham, s.mesajlar.join(" · ")]),
      ...(paket?.disarida ?? []).map((d) => [...d.s.ham, d.sebep]),
    ];
    downloadBlob(new Blob([csvYaz(satirlar, okuma.ayrac)], { type: "text/csv" }), "parafon-ice-aktarma-sorunlu.csv");
  };

  const geriYatirim = okuma?.satirlar.filter((s) => s.tur === GERI_YATIRIM) ?? [];
  const desenden = geriYatirim.filter((s) => s.desendenGeriYatirim).length;
  const sorunluSayi = (okuma?.sorunlar.length ?? 0) + (paket?.disarida.length ?? 0);

  return (
    <Screen title={M.iceAktar.ekran.baslik} description={M.iceAktar.ekran.aciklama}>
      <div className="mx-auto grid max-w-4xl gap-4 p-4">
        <section className="pf-card space-y-3 p-4" aria-labelledby="ice-dosya" data-kart-bolum="form">
          <h2 id="ice-dosya" className="text-[14px] font-semibold text-ink">
            {M.iceAktar.ekran.dosya.baslik}
          </h2>
          <p className="text-[12px] text-muted">
            {M.iceAktar.ekran.dosya.aciklama}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <label className="pf-btn pf-btn-primary cursor-pointer">
              {M.iceAktar.ekran.dosya.sec}
              <input
                type="file"
                accept=".csv,text/csv,text/plain,.pdf,application/pdf"
                multiple
                className="sr-only"
                aria-label={M.iceAktar.ekran.dosya.secEtiket}
                onChange={(e) => {
                  const fs = [...(e.currentTarget.files ?? [])];
                  if (fs.length > 0) onDosyalar(fs);
                  e.currentTarget.value = "";
                }}
              />
            </label>
            <Button
              variant="ghost"
              onClick={() => downloadBlob(new Blob([sablonCsv()], { type: "text/csv" }), "parafon-ice-aktarma-sablon.csv")}
            >
              {M.iceAktar.ekran.dosya.sablonIndir}
            </Button>
            {dosya !== null && <span className="text-[12px] text-faint">{dosya.ad}</span>}
            {midas !== null && <span className="text-[12px] text-faint">{midas.belgeler.map((b) => b.ad).join(", ")}</span>}
          </div>
          {okuRun.error !== undefined && <InlineError error={okuRun.error} />}
          {pdfHata !== null && (
            <Notice tone="danger" title={M.iceAktar.ekran.dosya.ekstreOkunamadi}>
              <p>{pdfHata}</p>
            </Notice>
          )}
          {pdfBekleyen !== null && (
            <form
              className="space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                void okuRun.run(() => pdfleriOku(pdfBekleyen.dosyalar, pdfParola));
              }}
            >
              <Field
                label={M.iceAktar.ekran.dosya.parola}
                htmlFor="ice-pdf-parola"
                hint={M.iceAktar.ekran.dosya.parolaIpucu}
                {...(pdfBekleyen.yanlis ? { error: M.iceAktar.ekran.dosya.parolaTutmadi } : {})}
              >
                <input
                  id="ice-pdf-parola"
                  type="password"
                  autoComplete="off"
                  className="pf-input"
                  value={pdfParola}
                  onChange={(e) => setPdfParola(e.currentTarget.value)}
                />
              </Field>
              <Button type="submit" variant="primary" disabled={pdfParola === ""}>
                {M.iceAktar.ekran.dosya.ac}
              </Button>
            </form>
          )}
          {midas !== null && (
            <Notice tone="info" title={M.iceAktar.ekran.dosya.tanindi(MIDAS_AD)}>
              <p>
                {M.iceAktar.ekran.dosya.midasAciklama(
                  midas.belgeler.map((b) => `${b.ad} (${b.paraBirimi}, ${b.donem})`).join("; "),
                  midas.atlanan,
                )}
              </p>
            </Notice>
          )}
          {okuma !== null && midas === null && dosya?.kodlama !== "pdf" && (
            <Field label={M.iceAktar.ekran.dosya.sayiBicimi} htmlFor="ice-bicim" hint={okuma.bicimOnerisi === null ? M.iceAktar.ekran.dosya.bicimAnlasilamadi : M.iceAktar.ekran.dosya.bicimOnerildi}>
              <Select
                id="ice-bicim"
                value={bicim ?? "tr"}
                onChange={(e) => setBicim(e.currentTarget.value as SayiBicimi)}
                options={[
                  { value: "tr", label: M.iceAktar.ekran.dosya.bicimTr },
                  { value: "en", label: M.iceAktar.ekran.dosya.bicimEn },
                ]}
              />
            </Field>
          )}
          {okuma !== null && okuma.eksikSutunlar.length > 0 && (
            <Notice tone="danger" title={M.iceAktar.ekran.dosya.sutunEksik}>
              <p>
                {M.iceAktar.ekran.dosya.sutunEksikAciklama(okuma.eksikSutunlar.join(", "), okuma.baslik.join(", ") || "—")}
              </p>
            </Notice>
          )}
          {okuma?.kaynakBicimi !== undefined && midas === null && (
            <Notice tone="info" title={M.iceAktar.ekran.dosya.tanindi(okuma.kaynakBicimi)}>
              <p>
                {M.iceAktar.ekran.dosya.oncesiTur(okuma.paraBirimi ?? "")}
                {okuma.paraBirimi === "TRY" ? M.iceAktar.ekran.dosya.oncesiTefas : ""}
                {M.iceAktar.ekran.dosya.oncesiDengeleme}
                {(okuma.yokSayilan ?? []).length > 0 ? M.iceAktar.ekran.dosya.oncesiYokSayilan((okuma.yokSayilan ?? []).join(", ")) : ""}
                {dosya?.kodlama === "windows-1254" ? M.iceAktar.ekran.dosya.windows1254 : ""}
                {dosya?.kodlama === "pdf" ? M.iceAktar.ekran.dosya.pdfTablo : ""}
              </p>
            </Notice>
          )}
          {okuma !== null && midas === null && okuma.bilinmeyenSutunlar.length > 0 && (
            <p className="text-[12px] text-muted">{M.iceAktar.ekran.dosya.bilinmeyenSutunlar(okuma.bilinmeyenSutunlar.join(", "))}</p>
          )}
        </section>

        {okuma !== null && okuma.eksikSutunlar.length === 0 && (
          <>
            <section className="pf-card space-y-3 p-4" aria-labelledby="ice-hesaplar" data-kart-bolum="ozet">
              <h2 id="ice-hesaplar" className="text-[14px] font-semibold text-ink">
                {M.iceAktar.ekran.hesaplar.baslik}
              </h2>
              {hesaplar.length === 0 && <p className="text-[12px] text-muted">{M.iceAktar.ekran.hesaplar.satirYok}</p>}
              {hesaplar.map((h) => {
                const sec = hesapSecimi[h.anahtar];
                return (
                  <div key={h.anahtar} className="grid gap-2 border-t border-line pt-2 sm:grid-cols-3" role="group" aria-label={M.iceAktar.ekran.hesaplar.grup(h.ad)}>
                    <p className="text-[13px] text-ink">
                      <strong>{h.ad}</strong>
                      {h.kimlik !== null && <span className="block text-[11px] text-faint">{M.iceAktar.ekran.hesaplar.kimlik(h.kimlik)}</span>}
                    </p>
                    <Field label={M.iceAktar.ekran.hesaplar.buHesap} htmlFor={`ice-h-${h.anahtar}`}>
                      <Select
                        id={`ice-h-${h.anahtar}`}
                        value={sec?.tur === "var" ? sec.id : sec?.tur === "secilmedi" ? "" : "yeni"}
                        onChange={(e) => {
                          const v = e.currentTarget.value;
                          if (v === "") return;
                          hesapSec(h.anahtar, v === "yeni" ? { tur: "yeni", kind: "araci_kurum", currency: enSik(h.paralar, "TRY"), ad: h.ad } : { tur: "var", id: v });
                        }}
                        options={[
                          ...(sec?.tur === "secilmedi" ? [{ value: "", label: M.iceAktar.ekran.hesaplar.secin }] : []),
                          { value: "yeni", label: M.iceAktar.ekran.hesaplar.yeniEkle },
                          ...mevcutHesaplar.map((a) => ({ value: a.id, label: M.iceAktar.ekran.hesaplar.varOlan(a.name, a.devreDisi) })),
                        ]}
                      />
                    </Field>
                    {sec?.tur === "var" && mevcutHesaplar.find((a) => a.id === sec.id)?.devreDisi === true && (
                      <div className="sm:col-span-3">
                        <Notice tone="warn" title={M.iceAktar.ekran.hesaplar.devreDisiBaslik}>
                          <p>
                            {M.iceAktar.ekran.hesaplar.devreDisiAciklama}
                          </p>
                        </Notice>
                      </div>
                    )}
                    {sec?.tur === "secilmedi" && (
                      <p className="text-[12px] text-muted sm:col-span-3">
                        {M.iceAktar.ekran.hesaplar.birdenCok}
                      </p>
                    )}
                    {sec?.tur === "yeni" && (
                      <div className="grid gap-2 sm:col-span-3 sm:grid-cols-3">
                        <Field
                          label={M.iceAktar.ekran.hesaplar.hesapAdi}
                          htmlFor={`ice-ha-${h.anahtar}`}
                          hint={M.iceAktar.ekran.hesaplar.araciKurum(h.ad)}
                          {...(sec.ad.trim() === "" ? { error: M.iceAktar.ekran.hesaplar.adBos } : {})}
                        >
                          <input
                            id={`ice-ha-${h.anahtar}`}
                            className="pf-input"
                            maxLength={80}
                            value={sec.ad}
                            onChange={(e) => {
                              const ad = e.currentTarget.value;
                              hesapSec(h.anahtar, { ...sec, ad });
                            }}
                          />
                        </Field>
                        <Field label={M.iceAktar.ekran.hesaplar.anaParaBirimi} htmlFor={`ice-hp-${h.anahtar}`}>
                          <CurrencySelect
                            id={`ice-hp-${h.anahtar}`}
                            value={sec.currency}
                            onChange={(c: string) => hesapSec(h.anahtar, { ...sec, currency: c.toUpperCase() })}
                          />
                        </Field>
                      </div>
                    )}
                  </div>
                );
              })}
            </section>

            <section className="pf-card space-y-2 p-4" aria-labelledby="ice-varliklar" data-kart-bolum="form">
              <h2 id="ice-varliklar" className="text-[14px] font-semibold text-ink">
                {M.iceAktar.ekran.varliklar.baslik}
              </h2>
              {varliklar.map((v) => {
                const d = varlikDurumu[v.anahtar];
                const ad = v.v.piyasa === null ? v.v.kod : `${v.v.piyasa}:${v.v.kod}`;
                return (
                  <div key={v.anahtar} className="border-t border-line pt-2 text-[13px]" role="group" aria-label={M.iceAktar.ekran.varliklar.grup(ad)}>
                    <p className="font-semibold text-ink">{ad}</p>
                    {(d === undefined || d.tur === "araniyor") && <Spinner label={M.iceAktar.ekran.varliklar.araniyor} />}
                    {d?.tur === "aday" &&
                      d.adaylar.map((c) => (
                        <label key={c.instrumentId} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`ice-v-${v.anahtar}`}
                            checked={d.secili === c.instrumentId}
                            onChange={() => setVarlikDurumu((x) => ({ ...x, [v.anahtar]: { ...d, secili: c.instrumentId } }))}
                          />
                          {c.symbol} · {c.exchange} · {c.name} · {c.currency}
                        </label>
                      ))}
                    {d?.tur === "aday" && d.secili === null && <p className="text-[12px] text-warn">{M.iceAktar.ekran.varliklar.birdenCok}</p>}
                    {d?.tur === "yok" && (
                      <div className="space-y-1">
                        <p className="text-[12px] text-warn">{d.sebep}</p>
                        <Button onClick={() => setVarlikDurumu((x) => ({ ...x, [v.anahtar]: { tur: "elle" } }))}>{M.iceAktar.ekran.varliklar.elleEkle}</Button>
                        <p className="text-[11px] text-faint">{M.iceAktar.ekran.varliklar.secmezseniz}</p>
                      </div>
                    )}
                    {d?.tur === "elle" && <p className="text-[12px] text-muted">{M.iceAktar.ekran.varliklar.elle}</p>}
                  </div>
                );
              })}
              {varliklar.length === 0 && <p className="text-[12px] text-muted">{M.iceAktar.ekran.varliklar.yok}</p>}
            </section>

            <section className="pf-card space-y-3 p-4" aria-labelledby="ice-onizleme" data-kart-bolum="liste">
              <h2 id="ice-onizleme" className="text-[14px] font-semibold text-ink">
                {M.iceAktar.ekran.onizleme.baslik}
              </h2>
              <p className="text-[13px] text-ink">
                {M.iceAktar.ekran.onizleme.okundu(okuma.satirlar.length, sorunluSayi)}
              </p>
              {geriYatirim.length > 0 && (
                <p className="text-[13px] text-ink">
                  {M.iceAktar.ekran.onizleme.geriYatirim(geriYatirim.length, desenden)}
                </p>
              )}
              {paket !== null && paket.arsivden > 0 && (
                <p className="text-[13px] text-ink">
                  {M.iceAktar.ekran.onizleme.arsivden(paket.arsivden)}
                </p>
              )}
              {paket !== null && paket.disarida.some((d) => d.bekleniyor) && (
                <div className="space-y-1">
                  <p className="text-[12px] text-warn">
                    {M.iceAktar.ekran.onizleme.bekleniyor(paket.disarida.filter((d) => d.bekleniyor).length)}
                  </p>
                  <Button onClick={() => setArsiv({})}>{M.iceAktar.ekran.onizleme.yenidenSor}</Button>
                </div>
              )}
              {!hazir && <p className="text-[12px] text-muted">{M.iceAktar.ekran.onizleme.hazirDegil}</p>}
              {ozetSonucu !== null && midas?.ozet != null && (
                <div className="space-y-1" aria-label={M.iceAktar.ekran.onizleme.ozetEtiket}>
                  <p className="text-[13px] text-ink">
                    {M.iceAktar.ekran.onizleme.ozetBaslik(midas.ozet.tarih.split("-").reverse().join("."))}{" "}
                    {ozetSonucu.every((o) => o.tutuyor)
                      ? M.iceAktar.ekran.onizleme.ozetTutuyor(ozetSonucu.length)
                      : M.iceAktar.ekran.onizleme.ozetTutmuyor(ozetSonucu.filter((o) => !o.tutuyor).length)}
                  </p>
                  {ozetSonucu.some((o) => !o.tutuyor) && (
                    <ul className="space-y-0.5 text-[12px] text-ink">
                      {ozetSonucu
                        .filter((o) => !o.tutuyor)
                        .map((o) => (
                          <li key={o.sembol} className="flex items-center gap-1.5">
                            <span className="text-warn" aria-label={M.iceAktar.ekran.onizleme.uyari}>
                              <UyariIkonu tone="warn" />
                            </span>
                            <span>
                              {o.sembol}: {M.iceAktar.ekran.onizleme.ekstrede} <span className="pf-num">{o.ekstrede}</span>, {M.iceAktar.ekran.onizleme.kasada}{" "}
                              {o.kasada === null ? M.iceAktar.ekran.onizleme.eslesmedi : <span className="pf-num">{o.kasada}</span>}
                            </span>
                          </li>
                        ))}
                    </ul>
                  )}
                  {ozetSonucu.some((o) => !o.tutuyor) && (
                    <p className="text-[12px] text-muted">
                      {M.iceAktar.ekran.onizleme.oncekiDonemler}
                    </p>
                  )}
                </div>
              )}
              {onizleme !== null && (
                <ul className="list-disc pl-5 text-[13px] text-ink" aria-label={M.iceAktar.ekran.onizleme.sayilarEtiket}>
                  {(Object.keys(DURUM_ADI) as IceAktarmaDurumu[])
                    .filter((d) => hSay[d] + tSay[d] > 0)
                    .map((d) => (
                      <li key={d}>
                        {hSay[d] > 0 ? M.iceAktar.ekran.onizleme.hesapSayisi(hSay[d]) : ""}
                        {hSay[d] > 0 && tSay[d] > 0 ? M.iceAktar.ekran.onizleme.ve : ""}
                        {tSay[d] > 0 ? M.iceAktar.ekran.onizleme.hareketSayisi(tSay[d]) : ""} {DURUM_ADI[d]}
                      </li>
                    ))}
                </ul>
              )}
              {onizlemeHatasi !== undefined && <InlineError error={onizlemeHatasi} />}
              {sorunluSayi > 0 && (
                <details>
                  <summary className="cursor-pointer text-[13px] font-semibold text-ink">{M.iceAktar.ekran.onizleme.sorunlu}</summary>
                  <ul className="mt-2 space-y-1 text-[12px]">
                    {okuma.sorunlar.slice(0, 100).map((s) => (
                      <li key={s.satirNo}>
                        <span className="pf-fig text-faint">{M.iceAktar.ekran.onizleme.satir(s.satirNo)}</span> {s.mesajlar.join(" · ")}
                      </li>
                    ))}
                    {(paket?.disarida ?? []).map((d) => (
                      <li key={`d${d.s.satirNo}`}>
                        <span className="pf-fig text-faint">{M.iceAktar.ekran.onizleme.satir(d.s.satirNo)}</span> {d.sebep}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2 space-y-2">
                    <Button onClick={sorunluIndir}>{M.iceAktar.ekran.onizleme.sorunluIndir}</Button>
                    <SatirGeriBildirimi
                      bicimAdi={okuma.kaynakBicimi ?? M.iceAktar.ekran.onizleme.csvDosyasi}
                      satirlar={geriBildirimSatirlari}
                    />
                  </div>
                </details>
              )}
              {iceAktarmaKilidi !== null && <KilitliKabiliyet ne={iceAktarmaKilidi} aciklama={M.iceAktar.ekran.onizleme.kilitAciklama} />}
              {iceAktarmaKilidi === null && hesapKilidi && (
                <KilitliKabiliyet
                  ne={M.iceAktar.ekran.onizleme.kilitYeniHesap}
                  aciklama={M.iceAktar.ekran.onizleme.kilitHesapAciklama(etkinHesapSayisi, hSay.yeni, sinir.hesap ?? 0)}
                />
              )}
              <Button
                variant="primary"
                disabled={yazRun.pending || onizleme === null || yazilacak === 0 || yazmaKilitli}
                onClick={onYaz}
              >
                {yazRun.pending ? M.iceAktar.ekran.onizleme.yaziliyor : yazilacak === 0 ? M.iceAktar.ekran.onizleme.yazilacakYok : M.iceAktar.ekran.onizleme.iceAktar(yazilacak)}
              </Button>
              {yazRun.error !== undefined && <InlineError error={yazRun.error} />}
              {sonuc !== null && (
                <Notice tone="info" title={M.iceAktar.ekran.onizleme.tamamBaslik}>
                  <p>
                    {M.iceAktar.ekran.onizleme.tamam(
                      sayim(sonuc.s.accounts).yeni,
                      sayim(sonuc.s.transactions).yeni,
                      sayim(sonuc.s.transactions).guncellenecek + sayim(sonuc.s.accounts).guncellenecek,
                    )}
                  </p>
                  {sonuc.izlenemeyen.length > 0 && (
                    <p>{M.iceAktar.ekran.onizleme.izlenemeyen(sonuc.izlenemeyen.join(", "))}</p>
                  )}
                </Notice>
              )}
            </section>
          </>
        )}

        <EkstreMaskeKarti />
      </div>
    </Screen>
  );
}
